import { lazy, Suspense, useId, useMemo, useState } from "react";
import { toast } from "sonner";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { checkAmount, formatUsd } from "../household/moneyInput";
import { safeCsvText } from "../file/csvParse";
import { buildPlan, planToCsvRows } from "./studentLoanMath";
import { paymentDate, formatMonthYear, formatFullDate } from "../converters/paymentDates";
import { PLAN_LINEUP_LINE, IDR_AS_OF } from "./studentLoanIdr";
import { FirstPaymentDateField, PayoffDateNotice, useFirstPaymentDate } from "../converters/FirstPaymentDateField";

const StudentLoanExtras = lazy(() => import("./StudentLoanExtras"));

const TOOL_INFO_CONTENT = {
  what: `Calculates a student loan's standard monthly payment, payoff time, and total interest, and models how paying extra each month, or a one-time lump sum, changes both the payoff time and total interest paid. The amortization table, payoff date and CSV use real calendar dates that start from your first payment date.`,
  how: `Enter your loan balance, interest rate, and term, and your standard payment is calculated. Add an optional extra monthly payment and an optional one-time lump sum (and the month you will pay it) to see the payoff time and interest savings. Set the first payment date to the day repayment begins (after any grace period) to see the real date of every payment and your payoff date; it only labels the schedule and does not change the amounts.`,
  tip: `The main results model standard fixed repayment. Below them you can add your income to see the Repayment Assistance Plan (and IBR for older loans) beside it, or a refinance, with the plan list dated. Check studentaid.gov for your own loans.`,
};

function Field({ label, value, onChange, error, hint, inputMode = "decimal" }: { label: string; value: string; onChange: (v: string) => void; error?: string; hint?: string; inputMode?: "decimal" | "numeric" }) {
  const id = useId();
  return (
    <div>
      <Label htmlFor={id} className="mb-1 block text-sm">{label}</Label>
      <Input id={id} value={value} inputMode={inputMode} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} aria-describedby={error ? `${id}-err` : undefined} className={error ? "border-destructive" : ""} />
      {error ? <p id={`${id}-err`} role="alert" className="text-xs text-destructive mt-1">{error}</p> : hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
    </div>
  );
}

export default function StudentLoanCalculator() {
  const [principal, setPrincipal] = useState("30000");
  const [rate, setRate] = useState("5.5");
  const [termYears, setTermYears] = useState("10");
  const [extraMonthly, setExtraMonthly] = useState("0");
  const [lumpSum, setLumpSum] = useState("0");
  const [lumpMonth, setLumpMonth] = useState("1");
  const [showAll, setShowAll] = useState(false);
  const fp = useFirstPaymentDate();
  const first = fp.first;

  const checks = {
    principal: checkAmount(principal, { label: "Loan balance", min: 0.01 }),
    rate: checkAmount(rate, { label: "Interest rate", max: 100 }),
    term: checkAmount(termYears, { label: "Term", integer: true, min: 1, max: 50 }),
    extra: checkAmount(extraMonthly, { label: "Extra payment", blank: 0 }),
    lump: checkAmount(lumpSum, { label: "Lump sum", blank: 0 }),
    lumpMonth: checkAmount(lumpMonth, { label: "Lump-sum month", blank: 1, integer: true, min: 1, max: 600 }),
  };
  const valid = Object.values(checks).every((c) => c.value !== null);

  const plans = useMemo(() => {
    if (!valid) return null;
    const base = { principal: checks.principal.value!, aprPercent: checks.rate.value!, years: checks.term.value! };
    const standard = buildPlan(base);
    const custom = buildPlan({ ...base, extraMonthly: checks.extra.value!, lumpSum: checks.lump.value!, lumpMonth: checks.lumpMonth.value! });
    return { standard, custom };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid, principal, rate, termYears, extraMonthly, lumpSum, lumpMonth]);

  const { gate } = useLogToolUsage(!!plans);
  const hasExtras = (checks.extra.value ?? 0) > 0 || (checks.lump.value ?? 0) > 0;

  const downloadCsv = () => {
    if (!plans) return;
    downloadBlob(new Blob([safeCsvText(planToCsvRows(plans.custom.rows, first))], { type: "text/csv;charset=utf-8" }), "student-loan-amortization.csv");
    toast.success("Amortization table saved");
  };

  const rows = plans?.custom.rows ?? [];
  const shown = showAll ? rows : rows.slice(0, 12);

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Field label="Loan Balance ($)" value={principal} onChange={setPrincipal} error={checks.principal.error} />
          <Field label="Interest Rate (% APR)" value={rate} onChange={setRate} error={checks.rate.error} hint="Between 0 and 100." />
          <Field label="Standard Term (years)" value={termYears} onChange={setTermYears} error={checks.term.error} inputMode="numeric" hint="Whole years, 1 to 50." />
          <Field label="Extra Monthly Payment ($, optional)" value={extraMonthly} onChange={setExtraMonthly} error={checks.extra.error} />
          <FirstPaymentDateField id="sl-first-payment" state={fp} help="The date your first payment is due, usually after your grace period ends (often 6 months after you leave school). Later payments fall on the same day each month. It only puts dates on the table and the payoff; it does not change your payment or interest. Clear it to use the 1st of next month." />
          <div className="grid grid-cols-2 gap-3">
            <Field label="One-time lump sum ($, optional)" value={lumpSum} onChange={setLumpSum} error={checks.lump.error} />
            <Field label="Paid in month #" value={lumpMonth} onChange={setLumpMonth} error={checks.lumpMonth.error} inputMode="numeric" />
          </div>
          <p className="text-xs text-muted-foreground">
            Grace periods and interest: on many loans, interest that builds up during a grace period or deferment is capitalized (added to the balance) when repayment starts. If that applies to you, enter the larger balance you will owe at that point.
          </p>
        </div>

        <div className="space-y-3" data-result>
          {!plans && <p role="status" className="text-sm text-muted-foreground py-4">Fix the red boxes to see your results.</p>}
          {plans && <>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-primary">{formatUsd(plans.standard.standardPayment, 2)}</p>
              <p className="text-xs text-muted-foreground mt-1">Standard Monthly Payment</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold">{Math.floor(plans.custom.payoffMonths / 12)}y {plans.custom.payoffMonths % 12}m</p>
              <p className="text-xs text-muted-foreground mt-1">Payoff Time{hasExtras ? " (with extra payments)" : ""}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{formatUsd(plans.custom.totalInterest)}</p>
              <p className="text-xs text-muted-foreground mt-1">Total Interest Paid</p>
            </Card>
            <PayoffDateNotice first={first} payments={plans.custom.payoffMonths} testId="sl-payoff" />
            {hasExtras && (
              <Card className="p-4 text-center border-green-200">
                <p className="text-lg font-bold text-green-600">{Math.max(0, plans.standard.payoffMonths - plans.custom.payoffMonths)} months earlier</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Payoff moved up by extra payments — saves {formatUsd(Math.max(0, plans.standard.totalInterest - plans.custom.totalInterest))} in interest
                </p>
                <p className="text-xs text-muted-foreground mt-1" data-testid="sl-extra-dates">
                  New payoff date {formatMonthYear(paymentDate(first, plans.custom.payoffMonths - 1))} instead of {formatMonthYear(paymentDate(first, plans.standard.payoffMonths - 1))}.
                </p>
              </Card>
            )}
          </>}
        </div>
      </div>

      {plans && (
        <section className="mt-6" aria-label="Amortization table">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h2 className="text-sm font-semibold">Amortization table ({rows.length} months)</h2>
            <Button type="button" size="sm" variant="outline" onClick={downloadCsv}><Download className="h-4 w-4 mr-1.5" />Download CSV</Button>
          </div>
          <div className="overflow-x-auto rounded-md border" data-no-copy>
            <table className="w-full text-xs tabular-nums">
              <thead className="bg-muted/50 text-left">
                <tr><th className="p-2">Month</th><th className="p-2">Date</th><th className="p-2 text-right">Payment</th><th className="p-2 text-right">Interest</th><th className="p-2 text-right">Principal</th><th className="p-2 text-right">Balance</th></tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.month} className="border-t">
                    <td className="p-2">{r.month}</td>
                    <td className="p-2 whitespace-nowrap" data-testid="sl-row-date">{formatFullDate(paymentDate(first, r.month - 1))}</td>
                    <td className="p-2 text-right">{formatUsd(r.payment, 2)}</td>
                    <td className="p-2 text-right">{formatUsd(r.interest, 2)}</td>
                    <td className="p-2 text-right">{formatUsd(r.principal, 2)}</td>
                    <td className="p-2 text-right">{formatUsd(r.balance, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 12 && (
            <Button type="button" size="sm" variant="ghost" className="mt-2" onClick={() => setShowAll((s) => !s)}>
              {showAll ? "Show first 12 months" : `Show all ${rows.length} months`}
            </Button>
          )}
        </section>
      )}

      {plans && (
        <Suspense fallback={<p className="text-xs text-muted-foreground mt-6" role="status">Loading plan comparison...</p>}>
          <StudentLoanExtras principal={checks.principal.value!} aprPercent={checks.rate.value!} years={checks.term.value!} standard={plans.standard} gate={gate} />
        </Suspense>
      )}
      <p className="text-xs text-muted-foreground mt-4" data-testid="sl-footer">
        The results above are standard fixed repayment. {PLAN_LINEUP_LINE} (Plan list checked {IDR_AS_OF}.)
      </p>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
